'use client'

import { MorphoLogo, Text } from '@repo/ui'
import { Github, Linkedin, type LucideProps, Twitter } from 'lucide-react'
import React from 'react'

type SocialIconProps = LucideProps

const footerNavigation = {
  developers: [
    { name: 'Github', href: 'https://github.com/morpho-org' },
    { name: 'Bug bounty', href: 'https://immunefi.com/bounty/morpho/' },
    { name: 'Audits', href: 'https://docs.morpho.org/security-reviews' }
  ],
  ecosystem: [
    { name: 'Docs', href: 'https://docs.morpho.org/' },
    { name: 'Morpho App', href: 'https://app.morpho.org/' },
    { name: 'Optimizer App', href: 'https://optimizers.morpho.org/' },
    { name: 'Brand Kit', href: 'https://brand.morpho.org/' }
  ],
  community: [
    { name: 'Governance', href: 'https://forum.morpho.org/' },
    { name: 'X', href: 'https://x.com/MorphoLabs' },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/morpho-association/' }
  ],
  discover: [
    { name: 'Dune', href: 'https://docs.morpho.org/apis/dune/morpho-blue' },
    { name: 'Warden', href: 'https://www.warden.finance/' },
    { name: 'Token Terminal', href: 'https://tokenterminal.com/terminal/projects/morpho' },
    { name: 'DeFiLlama', href: 'https://defillama.com/protocol/morpho' }
  ],
  legal: [
    { name: 'Terms of Use', href: 'https://morpho.org/terms-of-use/' },
    { name: 'Privacy Policy', href: 'https://morpho.org/privacy-policy/' }
  ],
  social: [
    {
      name: 'GitHub',
      href: 'https://github.com/morpho-org',
      icon: (props: SocialIconProps) => <Github {...props} />
    },
    {
      name: 'X',
      href: 'https://x.com/MorphoLabs',
      icon: (props: SocialIconProps) => <Twitter {...props} />
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/morpho-association/',
      icon: (props: SocialIconProps) => <Linkedin {...props} />
    }
  ]
}

type AppFooterProps = {
  className?: string
}

const LinkList: React.FC<{
  title: string
  items: readonly { name: string; href: string }[]
}> = ({ title, items }) => (
  <nav aria-label={title} className="flex flex-col gap-4">
    <Text size="md">{title}</Text>
    <ul className="space-y-px">
      {items.map(item => (
        <li key={item.name}>
          <a
            className="text-muted-foreground hover:text-foreground inline-block py-1.5 text-sm transition-colors duration-150"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.name}
          </a>
        </li>
      ))}
    </ul>
  </nav>
)

export function AppFooter({ className = '' }: AppFooterProps) {
  return (
    <footer className={className}>
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Brand / Intro */}
          <div className="flex flex-col gap-6 md:col-span-4 lg:col-span-5">
            <div className="flex items-center gap-3">
              <MorphoLogo />
            </div>
            <Text className="text-muted-foreground">
              A decentralized, noncustodial lending protocol implemented for the Ethereum Virtual
              Machine.
            </Text>

            {/* Social icons */}
            <div className="flex items-center gap-4">
              {footerNavigation.social.map(s => (
                <a key={s.name} aria-label={s.name} href={s.href} className="inline-flex p-1.5">
                  {s.icon({ width: 20, height: 20 })}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 md:col-span-8 lg:col-span-7">
            <LinkList title="Developers" items={footerNavigation.developers} />
            <LinkList title="Ecosystem" items={footerNavigation.ecosystem} />
            <LinkList title="Community" items={footerNavigation.community} />
            <LinkList title="Discover" items={footerNavigation.discover} />
            <LinkList title="Legal" items={footerNavigation.legal} />
          </div>
        </div>
      </div>
    </footer>
  )
}
